// src/pages/AdminUserManagement.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  Container,
  Paper,
  TableContainer,
  Chip,
  CircularProgress,
  Alert,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  TableSortLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BlockIcon from '@mui/icons-material/Block';
import { Link } from 'react-router-dom';

interface User {
  _id: string;
  email: string;
  name: string;
  role: string;
  lastLogin?: string;
  createdAt?: string;
}

interface ArtistBand {
  _id: string;
  artistName: string;
  status: string;
  isDraft: boolean;
  publishedTo: { strandId: string }[];
  createdAt: string;
  mainImage: string | null;
  views: number;
}

interface Artist extends User {
  bands: ArtistBand[];
  bandCount: number;
  totalViews: number;
}

interface PersonnelLink {
  exists: boolean;
  personnelId?: string;
  name?: string;
}

interface PendingBand {
  _id: string;
  artistName: string;
  status: string;
  mainImage: string | null;
  genres: string[];
  createdAt: string;
  createdBy: { _id: string; name: string; email: string } | null;
  about: string;
}

type SortField = 'name' | 'email' | 'role';
type ArtistSortField = 'name' | 'email' | 'bandCount' | 'totalViews';
type SortDirection = 'asc' | 'desc';

const AdminUserManagement: React.FC = () => {
  const { user } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // All users state
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [personnelLinks, setPersonnelLinks] = useState<Map<string, PersonnelLink>>(new Map());
  const [usersLoading, setUsersLoading] = useState(true);

  // Artists state
  const [artists, setArtists] = useState<Artist[]>([]);
  const [filteredArtists, setFilteredArtists] = useState<Artist[]>([]);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const [artistSortField, setArtistSortField] = useState<ArtistSortField>('name');
  const [artistSortDirection, setArtistSortDirection] = useState<SortDirection>('asc');
  const [artistSearch, setArtistSearch] = useState('');
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [artistDrawerOpen, setArtistDrawerOpen] = useState(false);

  // Shared state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  // User details drawer
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Revoke artist dialog
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [artistToRevoke, setArtistToRevoke] = useState<Artist | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Pending bands state
  const [pendingBands, setPendingBands] = useState<PendingBand[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [approvingLegacy, setApprovingLegacy] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 1 && artists.length === 0 && !artistsLoading) {
      fetchArtists();
    }
    if (activeTab === 4 && pendingBands.length === 0 && !pendingLoading) {
      fetchPendingBands();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
      await checkPersonnelLinks(response.data);
    } catch (err: any) {
      setError('Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchArtists = async () => {
    try {
      setArtistsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
      setFilteredArtists(response.data);
    } catch (err: any) {
      setError('Failed to fetch artists');
    } finally {
      setArtistsLoading(false);
    }
  };

  const fetchPendingBands = async () => {
    try {
      setPendingLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/bands/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingBands(response.data);
    } catch {
      setError('Failed to fetch pending bands');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleReviewBand = async (bandId: string, status: 'approved' | 'rejected') => {
    try {
      setReviewing(bandId);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/bands/${bandId}/review`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingBands(prev => prev.filter(b => b._id !== bandId));
      // Update the artist drawer if it's open
      if (selectedArtist) {
        setArtists(prev => prev.map(a => ({
          ...a,
          bands: a.bands.map(b => b._id === bandId ? { ...b, status } : b),
        })));
        setSelectedArtist(prev => prev ? {
          ...prev,
          bands: prev.bands.map(b => b._id === bandId ? { ...b, status } : b),
        } : null);
      }
      setSuccess(`Band ${status === 'approved' ? 'approved and now live on imc.show' : 'rejected'}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch {
      setError(`Failed to ${status} band`);
    } finally {
      setReviewing(null);
    }
  };

  const handleApproveLegacy = async () => {
    try {
      setApprovingLegacy(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/bands/approve-legacy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`${response.data.approved} existing bands approved and now live`);
      setTimeout(() => setSuccess(''), 5000);
      // Refresh pending list
      await fetchPendingBands();
    } catch {
      setError('Failed to approve legacy bands');
    } finally {
      setApprovingLegacy(false);
    }
  };

  const checkPersonnelLinks = async (userList: User[]) => {
    const token = localStorage.getItem('token');
    const links = new Map<string, PersonnelLink>();
    for (const u of userList) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/personnel/search?email=${encodeURIComponent(u.email)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && response.data.length > 0) {
          links.set(u._id, { exists: true, personnelId: response.data[0]._id, name: response.data[0].name });
        } else {
          links.set(u._id, { exists: false });
        }
      } catch {
        links.set(u._id, { exists: false });
      }
    }
    setPersonnelLinks(links);
  };

  // Filter + sort all users
  useEffect(() => {
    let filtered = users.filter(u =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    filtered.sort((a, b) => {
      const aVal = a[sortField].toLowerCase();
      const bVal = b[sortField].toLowerCase();
      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    setFilteredUsers(filtered);
  }, [searchTerm, users, roleFilter, sortField, sortDirection]);

  // Filter + sort artists
  useEffect(() => {
    let filtered = artists.filter(a =>
      a.name.toLowerCase().includes(artistSearch.toLowerCase()) ||
      a.email.toLowerCase().includes(artistSearch.toLowerCase())
    );
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;
      if (artistSortField === 'bandCount') {
        aVal = a.bandCount; bVal = b.bandCount;
      } else if (artistSortField === 'totalViews') {
        aVal = a.totalViews; bVal = b.totalViews;
      } else {
        aVal = (a[artistSortField] || '').toLowerCase();
        bVal = (b[artistSortField] || '').toLowerCase();
      }
      return artistSortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    setFilteredArtists(filtered);
  }, [artistSearch, artists, artistSortField, artistSortDirection]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, role: newRole } : u));
      setSuccess('User role updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to update user role');
    }
  };

  const handleRevokeArtist = async () => {
    if (!artistToRevoke) return;
    try {
      setRevoking(true);
      const token = localStorage.getItem('token');
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/users/${artistToRevoke._id}/role`,
        { role: 'guest' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setArtists(prev => prev.filter(a => a._id !== artistToRevoke._id));
      setSuccess(`Artist access revoked for "${artistToRevoke.name}" — role set to Guest`);
      setTimeout(() => setSuccess(''), 4000);
      setRevokeDialogOpen(false);
      setArtistDrawerOpen(false);
      setArtistToRevoke(null);
      // Refresh users list if loaded
      if (users.length > 0) {
        setUsers(prev => prev.map(u => u._id === artistToRevoke._id ? { ...u, role: 'guest' } : u));
      }
    } catch {
      setError('Failed to revoke artist access');
    } finally {
      setRevoking(false);
    }
  };

  const openDeleteDialog = (userObj: User) => {
    setUserToDelete(userObj);
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeleteConfirmation('');
  };

  const handleDeleteUser = async () => {
    if (!userToDelete || deleteConfirmation !== userToDelete.email) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${userToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(prev => prev.filter(u => u._id !== userToDelete._id));
      setArtists(prev => prev.filter(a => a._id !== userToDelete._id));
      setSuccess(`User "${userToDelete.name}" deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
      closeDeleteDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleArtistSort = (field: ArtistSortField) => {
    if (artistSortField === field) {
      setArtistSortDirection(artistSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setArtistSortField(field);
      setArtistSortDirection('asc');
    }
  };

  const getRoleColor = (role: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      superadmin: 'error',
      admin: 'warning',
      gm: 'info',
      team: 'primary',
      judge: 'secondary',
      domain_user: 'info',
      guest: 'default',
      artist: 'success',
    };
    return colors[role] || 'default';
  };

  const ROLE_LABELS: Record<string, string> = {
    superadmin: 'Super Admin', admin: 'Admin', gm: 'GM', team: 'Team',
    judge: 'Judge', domain_user: 'Domain User', guest: 'Guest', artist: 'Artist',
  };
  const formatRole = (role: string) =>
    ROLE_LABELS[role] ?? role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getPersonnelLink = (userId: string) => personnelLinks.get(userId) || { exists: false };

  const tabUsers = (role: string) => users.filter(u => u.role === role);

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin' && user.role !== 'gm')) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to view this page.</Typography>
        </Alert>
      </Container>
    );
  }

  // ----------------------------------------------------------------
  // Shared table for Judges / Guests tabs (simple filtered view)
  // ----------------------------------------------------------------
  const renderSimpleTable = (roleUsers: User[], emptyLabel: string) => (
    <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Email</strong></TableCell>
            <TableCell><strong>Role</strong></TableCell>
            <TableCell><strong>Last Login</strong></TableCell>
            <TableCell><strong>Personnel</strong></TableCell>
            <TableCell><strong>Change Role</strong></TableCell>
            <TableCell align="right"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roleUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{emptyLabel}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            roleUsers.map(u => {
              const pLink = getPersonnelLink(u._id);
              return (
                <TableRow key={u._id} hover>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={formatRole(u.role)} color={getRoleColor(u.role)} size="small" />
                  </TableCell>
                  <TableCell>{formatDate(u.lastLogin)}</TableCell>
                  <TableCell>
                    {pLink.exists ? (
                      <Tooltip title={`Personnel record: ${pLink.name}`}>
                        <Link to={`/personnel/${pLink.personnelId}`} style={{ textDecoration: 'none' }}>
                          <Chip icon={<CheckCircleIcon />} label="Linked" color="success" size="small" clickable />
                        </Link>
                      </Tooltip>
                    ) : (
                      <Chip label="No Record" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={u.role}
                      onChange={e => handleRoleChange(u._id, e.target.value)}
                      size="small"
                      disabled={u._id === user?._id || u.role === 'superadmin'}
                    >
                      <MenuItem value="superadmin">Super Admin</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      {user?.role === 'superadmin' && <MenuItem value="gm">GM</MenuItem>}
                      <MenuItem value="team">Team</MenuItem>
                      <MenuItem value="judge">Judge</MenuItem>
                      <MenuItem value="domain_user">Domain User</MenuItem>
                      <MenuItem value="guest">Guest</MenuItem>
                      <MenuItem value="artist">Artist</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => { setSelectedUser(u); setDetailsDrawerOpen(true); }} color="primary">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send email">
                        <IconButton size="small" component="a" href={`mailto:${u.email}`} color="primary">
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={u._id === user?._id ? "Cannot delete yourself" : "Delete user"}>
                        <span>
                          <IconButton size="small" onClick={() => openDeleteDialog(u)} color="error" disabled={u._id === user?._id}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>User Management</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage user roles, permissions, and accounts
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label={`All Users (${users.length})`} />
          <Tab label={`Artists (${artists.length || users.filter(u => u.role === 'artist').length})`} />
          <Tab label={`Judges (${tabUsers('judge').length})`} />
          <Tab label={`Guests (${tabUsers('guest').length})`} />
          <Tab
            label={pendingBands.length > 0 ? `Pending Bands (${pendingBands.length})` : 'Pending Bands'}
            sx={{ color: pendingBands.length > 0 ? 'warning.main' : undefined }}
          />
        </Tabs>
      </Box>

      {/* ---- ALL USERS TAB ---- */}
      {activeTab === 0 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ flex: 3 }}
            />
            <FormControl sx={{ flex: 1, minWidth: 150 }}>
              <InputLabel>Filter by Role</InputLabel>
              <Select value={roleFilter} label="Filter by Role" onChange={e => setRoleFilter(e.target.value)}>
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="superadmin">Super Admin</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="gm">GM</MenuItem>
                <MenuItem value="team">Team</MenuItem>
                <MenuItem value="judge">Judge</MenuItem>
                <MenuItem value="domain_user">Domain User</MenuItem>
                <MenuItem value="guest">Guest</MenuItem>
                <MenuItem value="artist">Artist</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>
                        <strong>Name</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortField === 'email'} direction={sortField === 'email' ? sortDirection : 'asc'} onClick={() => handleSort('email')}>
                        <strong>Email</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortField === 'role'} direction={sortField === 'role' ? sortDirection : 'asc'} onClick={() => handleSort('role')}>
                        <strong>Role</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell><strong>Personnel</strong></TableCell>
                    <TableCell><strong>Change Role</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No users found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map(u => {
                      const pLink = getPersonnelLink(u._id);
                      return (
                        <TableRow key={u._id} hover>
                          <TableCell>{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Chip label={formatRole(u.role)} color={getRoleColor(u.role)} size="small" />
                          </TableCell>
                          <TableCell>
                            {pLink.exists ? (
                              <Tooltip title={`Personnel record: ${pLink.name}`}>
                                <Link to={`/personnel/${pLink.personnelId}`} style={{ textDecoration: 'none' }}>
                                  <Chip icon={<CheckCircleIcon />} label="Linked" color="success" size="small" clickable />
                                </Link>
                              </Tooltip>
                            ) : (
                              <Chip label="No Record" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={u.role}
                              onChange={e => handleRoleChange(u._id, e.target.value)}
                              size="small"
                              disabled={u._id === user?._id || u.role === 'superadmin'}
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              {user?.role === 'superadmin' && <MenuItem value="gm">GM</MenuItem>}
                              <MenuItem value="team">Team</MenuItem>
                              <MenuItem value="judge">Judge</MenuItem>
                              <MenuItem value="domain_user">Domain User</MenuItem>
                              <MenuItem value="guest">Guest</MenuItem>
                              <MenuItem value="artist">Artist</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title="View details">
                                <IconButton size="small" onClick={() => { setSelectedUser(u); setDetailsDrawerOpen(true); }} color="primary">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send email">
                                <IconButton size="small" component="a" href={`mailto:${u.email}`} color="primary">
                                  <EmailIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={u._id === user?._id ? "Cannot delete yourself" : "Delete user"}>
                                <span>
                                  <IconButton size="small" onClick={() => openDeleteDialog(u)} color="error" disabled={u._id === user?._id}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Total users: {filteredUsers.length}{searchTerm || roleFilter !== 'all' ? ` (filtered from ${users.length})` : ''}
            </Typography>
          </Box>
        </>
      )}

      {/* ---- ARTISTS TAB ---- */}
      {activeTab === 1 && (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search artists by name or email..."
              value={artistSearch}
              onChange={e => setArtistSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Box>

          {artistsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel active={artistSortField === 'name'} direction={artistSortField === 'name' ? artistSortDirection : 'asc'} onClick={() => handleArtistSort('name')}>
                        <strong>Artist</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={artistSortField === 'email'} direction={artistSortField === 'email' ? artistSortDirection : 'asc'} onClick={() => handleArtistSort('email')}>
                        <strong>Email</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={artistSortField === 'bandCount'} direction={artistSortField === 'bandCount' ? artistSortDirection : 'asc'} onClick={() => handleArtistSort('bandCount')}>
                        <strong>Bands</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={artistSortField === 'totalViews'} direction={artistSortField === 'totalViews' ? artistSortDirection : 'asc'} onClick={() => handleArtistSort('totalViews')}>
                        <strong>Total Views</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell><strong>Last Login</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredArtists.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No artists found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredArtists.map(a => (
                      <TableRow key={a._id} hover sx={{ cursor: 'pointer' }} onClick={() => { setSelectedArtist(a); setArtistDrawerOpen(true); }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MusicNoteIcon fontSize="small" color="success" />
                            {a.name}
                          </Box>
                        </TableCell>
                        <TableCell>{a.email}</TableCell>
                        <TableCell>
                          <Chip label={a.bandCount} size="small" color={a.bandCount > 0 ? 'primary' : 'default'} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon fontSize="small" color="action" />
                            <Typography variant="body2">{a.totalViews.toLocaleString()}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(a.lastLogin)}</TableCell>
                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => { setSelectedArtist(a); setArtistDrawerOpen(true); }} color="primary">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send email">
                              <IconButton size="small" component="a" href={`mailto:${a.email}`} color="primary">
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke artist access">
                              <IconButton size="small" onClick={() => { setArtistToRevoke(a); setRevokeDialogOpen(true); }} color="warning">
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={a._id === user?._id ? "Cannot delete yourself" : "Delete account"}>
                              <span>
                                <IconButton size="small" onClick={() => openDeleteDialog(a)} color="error" disabled={a._id === user?._id}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {filteredArtists.length} artist{filteredArtists.length !== 1 ? 's' : ''}{artistSearch ? ` (filtered from ${artists.length})` : ''}
              {' · '}
              {artists.reduce((s, a) => s + a.bandCount, 0)} bands total
              {' · '}
              {artists.reduce((s, a) => s + a.totalViews, 0).toLocaleString()} total views
            </Typography>
          </Box>
        </>
      )}

      {/* ---- JUDGES TAB ---- */}
      {activeTab === 2 && (
        <>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            renderSimpleTable(tabUsers('judge'), 'No judges found')
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">{tabUsers('judge').length} judge{tabUsers('judge').length !== 1 ? 's' : ''}</Typography>
          </Box>
        </>
      )}

      {/* ---- GUESTS TAB ---- */}
      {activeTab === 3 && (
        <>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            renderSimpleTable(tabUsers('guest'), 'No guests found')
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">{tabUsers('guest').length} guest{tabUsers('guest').length !== 1 ? 's' : ''}</Typography>
          </Box>
        </>
      )}

      {/* ---- PENDING BANDS TAB ---- */}
      {activeTab === 4 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              These are bands submitted by artists awaiting your approval. Approved bands go live on imc.show immediately.
            </Typography>
          </Box>

          {pendingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : pendingBands.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
              <MusicNoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">No bands pending review</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Band Name</strong></TableCell>
                    <TableCell><strong>Submitted By</strong></TableCell>
                    <TableCell><strong>Genres</strong></TableCell>
                    <TableCell><strong>Submitted</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingBands.map(band => (
                    <TableRow key={band._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {band.mainImage ? (
                            <Box
                              component="img"
                              src={band.mainImage}
                              alt={band.artistName}
                              sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <Box sx={{
                              width: 40, height: 40, borderRadius: 1, flexShrink: 0,
                              bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <MusicNoteIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                            </Box>
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{band.artistName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {band.genres.slice(0, 2).join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {band.createdBy ? (
                          <Box>
                            <Typography variant="body2">{band.createdBy.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{band.createdBy.email}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {band.genres.slice(0, 3).map(g => (
                            <Chip key={g} label={g} size="small" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(band.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="View band page">
                            <IconButton size="small" component={Link} to={`/band/${band._id}`} target="_blank" color="primary">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve — band goes live on imc.show">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleReviewBand(band._id, 'approved')}
                                disabled={reviewing === band._id}
                              >
                                {reviewing === band._id ? <CircularProgress size={16} /> : <ThumbUpIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject — band stays hidden">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReviewBand(band._id, 'rejected')}
                                disabled={reviewing === band._id}
                              >
                                <ThumbDownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {pendingBands.length} band{pendingBands.length !== 1 ? 's' : ''} awaiting review
            </Typography>
          </Box>
        </>
      )}

      {/* ---- DELETE DIALOG ---- */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <DeleteIcon />
            Confirm Account Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              This permanently deletes the user account. Their bands will not be deleted.
            </Typography>
          </Alert>
          {userToDelete && (
            <Box sx={{ mb: 3 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', mt: 1 }}>
                <Typography variant="body2"><strong>Name:</strong> {userToDelete.name}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {userToDelete.email}</Typography>
                <Typography variant="body2"><strong>Role:</strong> {formatRole(userToDelete.role)}</Typography>
              </Paper>
              {getPersonnelLink(userToDelete._id).exists && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">This user has a linked Personnel record which will NOT be deleted.</Typography>
                </Alert>
              )}
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" gutterBottom>Type the user's email to confirm:</Typography>
          {userToDelete && (
            <Box sx={{ mt: 1, mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>{userToDelete.email}</Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Type email to confirm"
            value={deleteConfirmation}
            onChange={e => setDeleteConfirmation(e.target.value)}
            error={deleteConfirmation.length > 0 && deleteConfirmation !== userToDelete?.email}
            helperText={deleteConfirmation.length > 0 && deleteConfirmation !== userToDelete?.email ? 'Email does not match' : ''}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={deleteConfirmation !== userToDelete?.email || deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- REVOKE ARTIST DIALOG ---- */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
            <BlockIcon />
            Revoke Artist Access
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This will change the user's role from Artist to Guest. Their bands will not be deleted.
            </Typography>
          </Alert>
          {artistToRevoke && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2"><strong>Name:</strong> {artistToRevoke.name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {artistToRevoke.email}</Typography>
              <Typography variant="body2"><strong>Bands:</strong> {artistToRevoke.bandCount}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)} disabled={revoking}>Cancel</Button>
          <Button
            onClick={handleRevokeArtist}
            color="warning"
            variant="contained"
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} /> : <BlockIcon />}
          >
            {revoking ? 'Revoking...' : 'Revoke Artist Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- USER DETAILS DRAWER (All Users / Judges / Guests) ---- */}
      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={() => { setDetailsDrawerOpen(false); setSelectedUser(null); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        {selectedUser && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">User Details</Typography>
              <IconButton onClick={() => { setDetailsDrawerOpen(false); setSelectedUser(null); }}>
                <InfoIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <List>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText primary="Name" secondary={selectedUser.name} />
              </ListItem>
              <ListItem>
                <ListItemIcon><EmailIcon /></ListItemIcon>
                <ListItemText primary="Email" secondary={<a href={`mailto:${selectedUser.email}`} style={{ color: 'inherit' }}>{selectedUser.email}</a>} />
              </ListItem>
              <ListItem>
                <ListItemIcon><BadgeIcon /></ListItemIcon>
                <ListItemText primary="Role" secondary={<Chip label={formatRole(selectedUser.role)} color={getRoleColor(selectedUser.role)} size="small" />} />
              </ListItem>
              <ListItem>
                <ListItemIcon><AccessTimeIcon /></ListItemIcon>
                <ListItemText primary="Last Login" secondary={formatDate(selectedUser.lastLogin)} />
              </ListItem>
              <ListItem>
                <ListItemIcon><AccessTimeIcon /></ListItemIcon>
                <ListItemText primary="Account Created" secondary={formatDate(selectedUser.createdAt)} />
              </ListItem>
              {getPersonnelLink(selectedUser._id).exists && (
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Personnel Record"
                    secondary={
                      <Link to={`/personnel/${getPersonnelLink(selectedUser._id).personnelId}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                        {getPersonnelLink(selectedUser._id).name}
                      </Link>
                    }
                  />
                </ListItem>
              )}
            </List>
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button fullWidth variant="outlined" startIcon={<EmailIcon />} component="a" href={`mailto:${selectedUser.email}`}>
                Send Email
              </Button>
              {selectedUser._id !== user?._id && (
                <Button fullWidth variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => { setDetailsDrawerOpen(false); openDeleteDialog(selectedUser); }}>
                  Delete
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* ---- ARTIST DETAILS DRAWER ---- */}
      <Drawer
        anchor="right"
        open={artistDrawerOpen}
        onClose={() => { setArtistDrawerOpen(false); setSelectedArtist(null); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        {selectedArtist && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">Artist Details</Typography>
              <IconButton onClick={() => { setArtistDrawerOpen(false); setSelectedArtist(null); }}>
                <InfoIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText primary="Name" secondary={selectedArtist.name} />
              </ListItem>
              <ListItem>
                <ListItemIcon><EmailIcon /></ListItemIcon>
                <ListItemText primary="Email" secondary={<a href={`mailto:${selectedArtist.email}`} style={{ color: 'inherit' }}>{selectedArtist.email}</a>} />
              </ListItem>
              <ListItem>
                <ListItemIcon><AccessTimeIcon /></ListItemIcon>
                <ListItemText primary="Last Login" secondary={formatDate(selectedArtist.lastLogin)} />
              </ListItem>
              <ListItem>
                <ListItemIcon><AccessTimeIcon /></ListItemIcon>
                <ListItemText primary="Joined" secondary={formatDate(selectedArtist.createdAt)} />
              </ListItem>
            </List>

            <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3 }}>
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center' }}>
                <Typography variant="h5" color="primary">{selectedArtist.bandCount}</Typography>
                <Typography variant="caption" color="text.secondary">Bands</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center' }}>
                <Typography variant="h5" color="primary">{selectedArtist.totalViews.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">Total Views</Typography>
              </Paper>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Bands ({selectedArtist.bandCount})
            </Typography>

            {selectedArtist.bands.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No bands created yet.</Typography>
            ) : (
              <Box sx={{ mb: 3 }}>
                {selectedArtist.bands.map(band => (
                  <Paper key={band._id} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {band.artistName || '(Untitled Draft)'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        {band.isDraft && <Chip label="Draft" size="small" variant="outlined" />}
                        {!band.isDraft && (
                          <Chip
                            label={band.status || 'pending'}
                            size="small"
                            color={band.status === 'approved' ? 'success' : band.status === 'rejected' ? 'error' : 'warning'}
                          />
                        )}
                        {band.publishedTo.length > 0 && (
                          <Chip label={`${band.publishedTo.length} strand${band.publishedTo.length > 1 ? 's' : ''}`} size="small" color="info" />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <VisibilityIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{band.views.toLocaleString()}</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Tooltip title="Open band admin page">
                      <IconButton size="small" component={Link} to={`/admin/bands/${band._id}`} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button fullWidth variant="outlined" startIcon={<EmailIcon />} component="a" href={`mailto:${selectedArtist.email}`}>
                Send Email
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                startIcon={<BlockIcon />}
                onClick={() => { setArtistToRevoke(selectedArtist); setRevokeDialogOpen(true); }}
              >
                Revoke Artist Access
              </Button>
              {selectedArtist._id !== user?._id && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => { setArtistDrawerOpen(false); openDeleteDialog(selectedArtist); }}
                >
                  Delete Account
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Container>
  );
};

export default AdminUserManagement;
